import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { SUPERVISOR_ROLES } from "@shared/core/types";
import { Login } from "./core/routes/login";
import { MfaRequired } from "./core/routes/mfa-required";
import { Home } from "./core/routes/home";
import { UsersPage } from "./core/routes/users";
import { SettingsPage } from "./core/routes/settings";
import { ProfilePage } from "./core/routes/profile";
import { AuditPage } from "./core/routes/audit";
import { Shell } from "./core/components/layout/shell";
import { useAuthStore } from "./core/stores/auth";
import { MODULES } from "./modules/index";
import { allowedFor, type AppRoute } from "./core/modules";

const CORE_ROUTES: AppRoute[] = [
  { path: "/utilisateurs", element: <UsersPage />, adminOnly: true },
  { path: "/reglages", element: <SettingsPage />, adminOnly: true },
  { path: "/journal", element: <AuditPage />, roles: SUPERVISOR_ROLES },
  { path: "/profil", element: <ProfilePage /> },
];

/** Partie connectée : shell + routes du noyau et des modules actifs. */
function Protected() {
  const { user, loading, refresh } = useAuthStore();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <div className="boot">Chargement…</div>;
  if (!user) return <Login />;
  if (user.mfaSetupRequired) return <MfaRequired />;

  const guard = (r: AppRoute) => (allowedFor(r, user.role) ? r.element : <Navigate to="/" replace />);

  return (
    <Shell modules={MODULES}>
      <Routes>
        <Route path="/" element={<Home modules={MODULES} />} />
        {[...CORE_ROUTES, ...MODULES.flatMap((m) => m.routes)].map((r) => (
          <Route key={r.path} path={r.path} element={guard(r)} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  const publicRoutes = MODULES.flatMap((m) => m.publicRoutes ?? []);
  return (
    <Routes>
      {publicRoutes.map((r) => (
        <Route key={r.path} path={r.path} element={r.element} />
      ))}
      <Route path="/*" element={<Protected />} />
    </Routes>
  );
}
